import React, { useState } from 'react';
import { Brain, Play, CheckCircle, AlertCircle, BarChart3, Target, TrendingUp } from 'lucide-react';
import { PredictionEngine } from '../../lib/predictionEngine';

interface TestCase {
  id: string;
  name: string;
  features: any;
  expectedRisk: 'low' | 'medium' | 'high';
  expectedRange: [number, number];
  description: string;
}

export const ModelTesting: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const testCases: TestCase[] = [
    {
      id: 'high-risk-1',
      name: 'High Risk: Young Patient, Psychiatry, Poor History',
      features: {
        historicalAttendance: 45,
        ageGroup: '18-25',
        department: 'Psychiatry',
        appointmentTime: '18:00',
        dayOfWeek: 5, // Friday
        leadTime: 0, // Same day
        visitType: 'Screening',
        location: 'North Clinic',
        seasonalFactor: 1.15 // Winter
      },
      expectedRisk: 'high',
      expectedRange: [70, 95],
      description: 'Young patient with poor attendance history, psychiatry appointment on Friday evening, same-day booking'
    },
    {
      id: 'low-risk-1',
      name: 'Low Risk: Senior Patient, Cardiology, Good History',
      features: {
        historicalAttendance: 95,
        ageGroup: '65+',
        department: 'Cardiology',
        appointmentTime: '09:00',
        dayOfWeek: 2, // Tuesday
        leadTime: 7, // One week
        visitType: 'Follow-up',
        location: 'Main Hospital',
        seasonalFactor: 0.95 // Summer
      },
      expectedRisk: 'low',
      expectedRange: [5, 25],
      description: 'Senior patient with excellent attendance, cardiology follow-up on Tuesday morning, planned in advance'
    },
    {
      id: 'medium-risk-1',
      name: 'Medium Risk: Middle-aged, Orthopedics, Average History',
      features: {
        historicalAttendance: 70,
        ageGroup: '35-45',
        department: 'Orthopedics',
        appointmentTime: '14:00',
        dayOfWeek: 3, // Wednesday
        leadTime: 3, // 3 days
        visitType: 'Consultation',
        location: 'Main Hospital',
        seasonalFactor: 1.0 // Spring/Fall
      },
      expectedRisk: 'medium',
      expectedRange: [35, 60],
      description: 'Middle-aged patient with average attendance, orthopedics consultation, moderate advance notice'
    },
    {
      id: 'edge-case-1',
      name: 'Edge Case: Perfect History but High-Risk Factors',
      features: {
        historicalAttendance: 100,
        ageGroup: '18-25',
        department: 'Psychiatry',
        appointmentTime: '19:00',
        dayOfWeek: 5, // Friday
        leadTime: 0, // Same day
        visitType: 'Screening',
        location: 'North Clinic',
        seasonalFactor: 1.15
      },
      expectedRisk: 'medium',
      expectedRange: [25, 50],
      description: 'Perfect attendance history should override other high-risk factors'
    },
    {
      id: 'consistency-test',
      name: 'Consistency Test: Same Input Multiple Times',
      features: {
        historicalAttendance: 80,
        ageGroup: '25-35',
        department: 'General Medicine',
        appointmentTime: '10:00',
        dayOfWeek: 2,
        leadTime: 5,
        visitType: 'Routine',
        location: 'Main Hospital',
        seasonalFactor: 1.0
      },
      expectedRisk: 'low',
      expectedRange: [15, 35],
      description: 'Should produce consistent results with minimal variance'
    }
  ];

  const runTests = async () => {
    setIsRunning(true);
    const results = [];

    for (const testCase of testCases) {
      // Run prediction multiple times to test consistency
      const predictions = [];
      for (let i = 0; i < 5; i++) {
        const result = PredictionEngine.predict(testCase.features);
        predictions.push(result);
      }

      const probabilities = predictions.map(p => p.noShowProbability);
      const avgProbability = probabilities.reduce((a, b) => a + b, 0) / probabilities.length;
      const variance = Math.sqrt(probabilities.reduce((sum, p) => sum + Math.pow(p - avgProbability, 2), 0) / probabilities.length);
      
      const riskLevels = predictions.map(p => p.riskLevel);
      const consistentRisk = riskLevels.every(r => r === riskLevels[0]);

      const inExpectedRange = avgProbability >= testCase.expectedRange[0] && avgProbability <= testCase.expectedRange[1];
      const correctRiskLevel = predictions[0].riskLevel === testCase.expectedRisk;

      results.push({
        ...testCase,
        avgProbability: Math.round(avgProbability),
        variance: Math.round(variance * 10) / 10,
        consistentRisk,
        inExpectedRange,
        correctRiskLevel,
        passed: inExpectedRange && correctRiskLevel && consistentRisk,
        predictions: predictions[0] // Store first prediction for factor analysis
      });

      // Add small delay to simulate real testing
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setTestResults(results);
    setIsRunning(false);
  };

  const passedTests = testResults.filter(r => r.passed).length;
  const totalTests = testResults.length;
  const passRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Model Testing & Validation</h1>
        <p className="text-gray-600 mt-2">Test the prediction model with known scenarios to validate accuracy</p>
      </div>

      <div className="bg-white rounded-lg border mb-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Prediction Model Validation</h3>
                <p className="text-gray-600">Run standardized test cases to verify model behavior</p>
              </div>
            </div>
            
            <button
              onClick={runTests}
              disabled={isRunning}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                isRunning
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isRunning ? (
                <>
                  <Brain className="w-4 h-4 animate-pulse" />
                  <span>Running Tests...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Run All Tests</span>
                </>
              )}
            </button>
          </div>
          
          {testResults.length > 0 && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-3 rounded-lg ${passRate >= 80 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center space-x-2">
                  {passRate >= 80 ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-medium ${passRate >= 80 ? 'text-green-800' : 'text-red-800'}`}>
                    Pass Rate: {passRate.toFixed(1)}%
                  </span>
                </div>
                <p className={`text-sm mt-1 ${passRate >= 80 ? 'text-green-700' : 'text-red-700'}`}>
                  {passedTests} of {totalTests} tests passed
                </p>
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Accuracy</span>
                </div>
                <p className="text-sm text-blue-700 mt-1">
                  Predictions within expected ranges
                </p>
              </div>
              
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-indigo-800">Consistency</span>
                </div>
                <p className="text-sm text-indigo-700 mt-1">
                  Low variance across multiple runs
                </p>
              </div>
            </div>
          )}
        </div>

        {testResults.length > 0 && (
          <div className="divide-y divide-gray-200">
            {testResults.map((result) => (
              <div key={result.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {result.passed ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                      <h4 className="font-semibold text-gray-900">{result.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        result.passed 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {result.passed ? 'PASSED' : 'FAILED'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{result.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Test Results</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average Probability:</span>
                        <span className="font-medium">{result.avgProbability}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expected Range:</span>
                        <span className="font-medium">{result.expectedRange[0]}-{result.expectedRange[1]}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Variance:</span>
                        <span className={`font-medium ${result.variance < 5 ? 'text-green-600' : 'text-yellow-600'}`}>
                          ±{result.variance}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Risk Level:</span>
                        <span className={`font-medium ${
                          result.correctRiskLevel ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {result.predictions.riskLevel} {result.correctRiskLevel ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Consistency:</span>
                        <span className={`font-medium ${
                          result.consistentRisk ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {result.consistentRisk ? 'Consistent ✓' : 'Inconsistent ✗'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Top Contributing Factors</h5>
                    <div className="space-y-2">
                      {result.predictions.factors.slice(0, 4).map((factor: any, index: number) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{factor.name}:</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${(factor.impact * factor.weight) * 200}%` }}
                              ></div>
                            </div>
                            <span className="font-medium w-8 text-right">
                              {Math.round(factor.impact * factor.weight * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Testing Methodology</h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p><strong>Range Testing:</strong> Verify predictions fall within expected probability ranges for known scenarios</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p><strong>Risk Classification:</strong> Ensure correct risk level assignment (low/medium/high)</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p><strong>Consistency Testing:</strong> Run same inputs multiple times to check for stable results</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p><strong>Edge Case Analysis:</strong> Test extreme scenarios and conflicting factors</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p><strong>Factor Validation:</strong> Verify that key factors contribute appropriately to predictions</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Validation Criteria</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-green-800 font-medium">Pass Rate Target</span>
              <span className="text-green-600 font-bold">≥ 80%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-blue-800 font-medium">Variance Threshold</span>
              <span className="text-blue-600 font-bold">≤ 5%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <span className="text-purple-800 font-medium">Risk Accuracy</span>
              <span className="text-purple-600 font-bold">100%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <span className="text-indigo-800 font-medium">Consistency</span>
              <span className="text-indigo-600 font-bold">Required</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};